'use client';

import { useQueries } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { useEffect } from 'react';
import { singletonHook } from 'react-singleton-hook';
import { toast } from 'react-toastify';
import {
    computeNextTrackedOrders,
    getIsRemoteOrderCompleted,
} from '@/entities/order/lib/computeNextTrackedOrders';
import { TrackedOrder, useTrackedOrders } from '@/entities/order/lib/useTrackedOrders';
import { OrderProgressState, orderStateMap } from '@/entities/order/ui/order-progress';
import { QueryApi } from '@/features/query/api';
import { Alert } from '@/shared/ui/alert';

export type TrackedOrdersData = {
    trackedOrders: TrackedOrder[];
    firstOrderData: ReturnType<
        typeof useQueries<ReturnType<typeof QueryApi.Order.queries.Order>[]>
    >['0']['data'];
    firstOrderState: OrderProgressState | null;
};

const initialState: TrackedOrdersData = {
    trackedOrders: [],
    firstOrderData: undefined,
    firstOrderState: null,
};

const useTrackedOrdersDataImpl = (): TrackedOrdersData => {
    const [trackedOrders, setTrackedOrders] = useTrackedOrders();
    const t = useTranslations();
    const ordersQueries = useQueries({
        queries: trackedOrders.map(({ id }) => ({
            ...QueryApi.Order.queries.Order(id),
        })),
    });

    const hasError = ordersQueries.some((q) => q.isError);
    useEffect(() => {
        if (hasError) {
            toast(({ closeToast }) => (
                <Alert
                    heading={t('CART_SUMMARY_BOX.ORDER_TRACKING_FAILED')}
                    type="error"
                    onClose={closeToast}
                />
            ));
        }
    }, [hasError, t]);

    const firstOrderData =
        ordersQueries.find(
            (query) => !getIsRemoteOrderCompleted(query.data?.status?.definition?.identifier ?? ''),
        )?.data ?? ordersQueries?.[0]?.data;

    useEffect(() => {
        const { orders, changed } = computeNextTrackedOrders(trackedOrders, ordersQueries);

        if (changed) {
            setTrackedOrders(orders);
        }
    }, [ordersQueries, trackedOrders, setTrackedOrders]);

    const firstOrderState = firstOrderData?.status?.definition?.identifier
        ? (orderStateMap[firstOrderData.status.definition.identifier] ?? null)
        : null;

    return {
        trackedOrders,
        firstOrderData,
        firstOrderState,
    };
};

export const useTrackedOrdersData = singletonHook(initialState, useTrackedOrdersDataImpl);
